import { CITY_CATALOG_A } from "./cities-catalog-a-data";
import { CITY_CATALOG_B } from "./cities-catalog-b-data";
import { CITY_CATALOG_C } from "./cities-catalog-c-data";
import { CITY_CATALOG_D } from "./cities-catalog-d-data";
import {
  CHOGHADIYA_INDEXABLE_CITY_SLUGS,
  PANCHANG_INDEXABLE_CITY_SLUGS,
  RAHU_KAAL_INDEXABLE_CITY_SLUGS,
  SHUBH_MUHURAT_INDEXABLE_CITY_SLUGS,
} from "./city-index-registry.generated";
import type { CityData } from "./cities-data.types";
import { citySunriseRange } from "./city-sunrise";
import { SHUBH_MUHURAT_CITY_NOTES } from "./shubh-muhurat-city-note-data";

export {
  CHOGHADIYA_INDEXABLE_CITY_SLUGS,
  PANCHANG_INDEXABLE_CITY_SLUGS,
  RAHU_KAAL_INDEXABLE_CITY_SLUGS,
  SHUBH_MUHURAT_INDEXABLE_CITY_SLUGS,
} from "./city-index-registry.generated";

// Re-exported so existing `import { CityData } from "./cities-data"` consumers
// keep working; the canonical definition lives in the leaf module to avoid the
// catalog↔aggregator import cycle (see cities-data.types.ts).
export type { CityData } from "./cities-data.types";

export const CITIES: CityData[] = [
  ...CITY_CATALOG_A,
  ...CITY_CATALOG_B,
  ...CITY_CATALOG_C,
  ...CITY_CATALOG_D,
];

export const CITY_MAP: Record<string, CityData> = Object.fromEntries(
  CITIES.map((c) => [c.slug, c])
);

export const CITY_SLUGS = CITIES.map((c) => c.slug);

/**
 * Build-time pre-render cohort for city timing pages. Every city still renders
 * on demand, but only this cohort is statically generated at build. Keep this
 * separate from Choghadiya index promotion: static warmth is a performance
 * concern; sitemap/robots promotion is an SEO value concern.
 *
 * Membership is DEMAND-LED, not a round number: a city joins only with proven
 * GSC ranking (impressions AND real clicks) for its Choghadiya page, never the
 * zero-demand long tail — which keeps the rendered long-tail copy out of the
 * index and avoids the near-duplicate-Choghadiya risk the 2026-06-03 hold-out
 * was protecting against (rule 49 + weekly indexable-value crawl).
 *
 * Cohorts:
 *   - Founding 12 (2026-06): the first proven Choghadiya cities.
 *   - 2026-06-20 (+25): demand-proven recovery batch — Choghadiya city pages
 *     that were noindex despite ≥1000 GSC impressions/28d AND ≥1 click each.
 *     These remain warm/static, but Choghadiya sitemap promotion is now a
 *     stricter, separate cohort below after the weekly live duplicate gate
 *     proved the broader rendered pages were still too similar.
 */
export const PREBUILD_CITY_SLUGS: readonly string[] = [
  // Founding proven cohort
  "mumbai",
  "new-delhi",
  "jaipur",
  "indore",
  "surat",
  "bangalore",
  "kolkata",
  "lucknow",
  "pune",
  "bhopal",
  "vadodara",
  "rajkot",
  // 2026-06-20 demand-proven Choghadiya recovery batch (≥1000 impr + ≥1 clk/28d)
  "hyderabad",
  "ahmedabad",
  "bikaner",
  "udaipur",
  "chennai",
  "ajmer",
  "kota",
  "nagpur",
  "ujjain",
  "pushkar",
  "raipur",
  "noida",
  "gandhinagar",
  "nashik",
  "chandigarh",
  "kanpur",
  "bhavnagar",
  "dehradun",
  "agra",
  "bhilwara",
  "patna",
  "faridabad",
  "ranchi",
  "sikar",
  "guruvayur",
];

/**
 * 2026-07-23 site-level suppression response (see
 * docs/ishvaram/seo-suppression-incident-and-recovery-2026-07-22.md): Google
 * applied a SITE-level serving suppression on 2026-07-21 ~23:30 IST; the
 * scaled city-timing footprint (1,278 URLs = 46% of the sitemap, 0.9% of
 * clicks) is the likely classifier input. Panchang / Rahu-Kaal / Shubh-Muhurat
 * therefore move from "all 198 data-rich cities" to EARNED allowlists — the
 * same promotion mechanism choghadiya has used since 2026-07-07. Seeded from
 * GSC 90d (2026-04-22..07-20, dataState=all): every city whose EN+HI pages
 * earned >= 2 clicks stays indexed (retains 95.6% of cohort clicks with 46%
 * fewer URLs); zero/near-zero-click cities render noindex,follow and drop from
 * the sitemap automatically (single-predicate contract). REVERSIBLE per city:
 * re-add a slug once its page earns demand + distinct copy (the Jaipur
 * pattern, page_quality_contract.yaml city_timing_detail).
 * Membership is generated from the locked, evidence-bearing city index
 * registry. Do not add slugs in this runtime module: the only admission path is
 * `uv run python execution/city_index_gate.py --promote` with rendered corpus
 * and engine-proof inputs.
 */

export const CITY_TIMING_CATEGORIES = [
  "choghadiya",
  "panchang",
  "rahu-kaal",
  "shubh-muhurat",
] as const;

export type CityTimingCategory = (typeof CITY_TIMING_CATEGORIES)[number];

const CITY_TIMING_CATEGORY_SET: ReadonlySet<string> = new Set(CITY_TIMING_CATEGORIES);
const CITY_TIMING_INDEXABLE_CITY_SLUG_SETS: Record<CityTimingCategory, ReadonlySet<string>> = {
  choghadiya: new Set(CHOGHADIYA_INDEXABLE_CITY_SLUGS),
  panchang: new Set(PANCHANG_INDEXABLE_CITY_SLUGS),
  "rahu-kaal": new Set(RAHU_KAAL_INDEXABLE_CITY_SLUGS),
  "shubh-muhurat": new Set(SHUBH_MUHURAT_INDEXABLE_CITY_SLUGS),
};

export function isCityTimingCategory(category: string): category is CityTimingCategory {
  return CITY_TIMING_CATEGORY_SET.has(category);
}

const CITY_TIMING_HUB_PATHS: Record<CityTimingCategory, Record<"en" | "hi", string>> = {
  choghadiya: { en: "/choghadiya/", hi: "/hi/choghadiya/" },
  panchang: { en: "/panchang/", hi: "/hi/panchang/" },
  "rahu-kaal": { en: "/rahu-kaal/", hi: "/hi/rahu-kaal/" },
  // English has a demand-earned same-day decision hub. Hindi city pages keep
  // their established event-planner fallback until a Hindi root independently
  // earns indexing; never manufacture a locale twin without demand.
  "shubh-muhurat": { en: "/shubh-muhurat/", hi: "/hi/muhurat/" },
};

export function cityTimingHubHref(
  category: CityTimingCategory,
  locale: "en" | "hi" = "en"
): string {
  return CITY_TIMING_HUB_PATHS[category][locale];
}

// ── Content helpers — used by [city]/page.tsx to fill template for thin cities ──

/**
 * The city's annual sunrise window, or `null` when the engine has no profile for it.
 *
 * Engine-canonical since 2026-07-29 (rules 24/29). The previous implementation
 * preferred a hand-authored catalog string and otherwise estimated the range from a
 * linear model; both were measurably wrong (see `city-sunrise.ts`). Callers must
 * render their sunrise clause conditionally — never substitute an estimate.
 */
export function getCitySunriseRange(city: CityData): string | null {
  return citySunriseRange(city.slug);
}

export function getCityRegionalNote(city: CityData): string {
  if (city.regional_note) return city.regional_note;
  const sr = getCitySunriseRange(city);
  const sunriseSentence = sr
    ? ` Sunrise ranges from ${sr.split("–")[0]} in summer to ${sr.split("–")[1]} in winter, calculated for ${city.name}'s exact coordinates (${city.latitude.toFixed(2)}°N, ${city.longitude.toFixed(2)}°E).`
    : ` Timings are calculated for ${city.name}'s exact coordinates (${city.latitude.toFixed(2)}°N, ${city.longitude.toFixed(2)}°E).`;
  return `${city.name}, ${city.state} follows Indian Standard Time (UTC+5:30).${sunriseSentence} The panchang, choghadiya, and rahu kaal timings above use these coordinates via Swiss Ephemeris for accurate daily muhurat timings. Local religious observances follow the Hindu lunisolar calendar as practised across ${city.state}.`;
}

export function getCityLocalFestivals(city: CityData): string[] {
  return city.local_festivals ?? ["Diwali", "Navratri", "Holi"];
}

/**
 * Muhurat-specific per-city locality paragraphs for shubh-muhurat/[city]
 * (+ /hi/ twin). Prefers a hand-authored, ceremony-context note
 * (SHUBH_MUHURAT_CITY_NOTES — the head cities); every other city falls back to
 * the generic regional note so all 198 pages render distinct per-city prose
 * instead of the template-identical explainer (rule 66 enrichment, the
 * 2026-07-21 suppression response). No computed Vedic facts here (rules 23/24).
 */
export function getCityMuhuratNote(city: CityData, locale: "en" | "hi" = "en"): readonly string[] {
  const note = SHUBH_MUHURAT_CITY_NOTES[city.slug];
  if (note) return locale === "hi" ? note.hi : note.en;
  return [getCityRegionalNote(city)];
}

/** True when the city has a hand-authored muhurat-specific note (not the fallback). */
export function cityHasAuthoredMuhuratNote(slug: string): boolean {
  return slug in SHUBH_MUHURAT_CITY_NOTES;
}

/**
 * A city timing page is INDEXABLE when it has genuine per-city content — a
 * regional note AND local festivals — on top of the server-rendered engine data
 * (sunrise/tithi/choghadiya/rahu-kaal computed from the city's coordinates, the
 * Drik-Panchang model). The engine data is the unique content that makes each
 * page non-duplicate (rule 49), so the data-rich predicate is now safe at scale
 * (it was held out 2026-06-03 only while the data rendered client-side).
 *
 * Watched post-deploy by the indexable-value gate (weekly-seo-index-protection),
 * the render-contract gate (rule 49), and the impression-regression guard
 * (rule 48). REVERSIBLE: tighten this predicate (e.g. back to a `PREBUILD_CITY_SLUGS`
 * allowlist) to pull cities out of the index.
 */
export function cityIsIndexable(city: CityData): boolean {
  return Boolean(city.regional_note && city.local_festivals && city.local_festivals.length > 0);
}

/**
 * Tool-specific promotion for city timing DETAIL pages.
 *
 * `cityIsIndexable` says a city has enough local data to be eligible in general,
 * but each timing tool still has to prove that its rendered page body is
 * distinct at scale. Choghadiya is held to its explicit promoted cohort because
 * the weekly indexable-value crawl found near-duplicate city pages in both EN
 * and HI. Since 2026-07-23 (site-level suppression response, see the allowlist
 * block above) Panchang/Rahu-Kaal/Shubh-Muhurat are ALSO earned allowlists —
 * no category keeps the broad 198-city cohort anymore.
 */
export function cityTimingPageIsIndexable(category: CityTimingCategory, city: CityData): boolean {
  if (!cityIsIndexable(city)) return false;
  return CITY_TIMING_INDEXABLE_CITY_SLUG_SETS[category].has(city.slug);
}

export function cityTimingSlugIsIndexable(category: string, citySlug: string): boolean {
  if (!isCityTimingCategory(category)) return false;
  const city = CITY_MAP[citySlug];
  return Boolean(city && cityTimingPageIsIndexable(category, city));
}

export function cityTimingDetailOrHubHref(
  category: CityTimingCategory,
  citySlug: string,
  locale: "en" | "hi" = "en"
): string {
  const prefix = locale === "hi" ? "/hi" : "";
  if (cityTimingSlugIsIndexable(category, citySlug)) {
    return `${prefix}/${category}/${citySlug}/`;
  }
  return cityTimingHubHref(category, locale);
}

export function cityTimingIndexableCities(category: CityTimingCategory): CityData[] {
  return CITIES.filter((city) => cityTimingPageIsIndexable(category, city));
}

// Data-rich cities are eligible for broad timing SEO. Individual tools can apply
// a stricter cohort with `cityTimingPageIsIndexable`.
export const SEO_CITIES = CITIES.filter(cityIsIndexable);

export const SEO_CITY_SLUGS = SEO_CITIES.map((c) => c.slug);

export const INDEXABLE_CITY_SLUGS: ReadonlySet<string> = new Set(SEO_CITY_SLUGS);
